import React, { useEffect, useState } from 'react';

import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { WalletAccountLabel } from '@pkg/components/Sections/Profile/WalletAccountLabel';

import { useManagerFilterProps } from '@app/pages/Prototype/NftManagerPanel/hooks';

import {
    ClearAllItem,
    RemoveItemButton,
    SelectedFilterItem,
    SelectedFilterName,
    SelectedFiltersListWrapper,
} from './styles';

interface CurrencyItem {
    currency: string;
    min?: number;
    max?: number;
}

interface SelectedFilter {
    category: string;
    value: string[] | CurrencyItem;
}

export function SelectedFiltersList() {
    const { t: translate } = useTranslation(['yat_nft_manager']);
    const [activeFilters, setActiveFilters] = useState<SelectedFilter[]>([]);
    const { currentFilters, onClearFilters, onRemoveFilter } = useManagerFilterProps();

    useEffect(() => {
        if (currentFilters) {
            const updatedFilters: SelectedFilter[] = [];
            let hasCurrencyInfo = false;

            Object.entries(currentFilters).map(([filterKey, filterValue]) => {
                if (filterKey === 'sortingField' || filterKey === 'sortingOrder') return;

                if (filterKey.startsWith('floorPrice')) {
                    hasCurrencyInfo = true;
                    return;
                }

                if (typeof filterValue === 'object') {
                    if (filterValue.length) {
                        updatedFilters.push({ category: filterKey, value: filterValue });
                    }
                }
            });

            if (hasCurrencyInfo) {
                if (
                    (currentFilters['floorPriceLe'] !== undefined || currentFilters['floorPriceGe'] !== undefined) &&
                    currentFilters['floorPriceCurrency'] !== undefined
                ) {
                    updatedFilters.push({
                        category: 'floorPrice',
                        value: {
                            currency: currentFilters['floorPriceCurrency'],
                            min: currentFilters['floorPriceGe'],
                            max: currentFilters['floorPriceLe'],
                        },
                    });
                }
            }

            setActiveFilters(updatedFilters);
        }
    }, [currentFilters]);

    // TODO: This component should be conditionally rendered,
    // and animate open/close like an <Accordion />,
    // when there are actually filters to render.
    return (
        <SelectedFiltersListWrapper>
            {activeFilters.map((filterType, activeFiltersIndex) => {
                if (filterType.category === 'floorPrice') {
                    const { currency, min, max } = filterType.value as CurrencyItem;

                    return (
                        <SelectedFilterItem key={activeFiltersIndex}>
                            <SelectedFilterName>
                                {`${translate('yat_nft_manager_filter_floor_price_selection')}${
                                    min ? `${translate('yat_nft_manager_filter_price_min').toLowerCase()} ${min}` : ''
                                } ${
                                    max ? `${translate('yat_nft_manager_filter_price_max').toLowerCase()} ${max}` : ''
                                } ${currency.toUpperCase()}`}
                            </SelectedFilterName>

                            <RemoveItemButton
                                aria-label="Remove Floor Price filter"
                                onClick={() => onRemoveFilter(filterType.category)}
                            >
                                <motion.span />
                                <motion.span />
                            </RemoveItemButton>
                        </SelectedFilterItem>
                    );
                }

                const categoryFilters = filterType.value as string[];

                if (filterType.category === 'ownerAddress') {
                    return categoryFilters.map((filterCategory, index) => (
                        <SelectedFilterItem key={`${filterCategory}-${index}`} $isOwnerAddress>
                            <SelectedFilterName style={{ display: 'inline-flex' }}>
                                <WalletAccountLabel account={filterCategory} />
                            </SelectedFilterName>

                            <RemoveItemButton onClick={() => onRemoveFilter(filterType.category, filterCategory)}>
                                <motion.span />
                                <motion.span />
                            </RemoveItemButton>
                        </SelectedFilterItem>
                    ));
                }

                return categoryFilters.map((filterCategory, index) => (
                    <SelectedFilterItem key={`${filterCategory}-${index}`}>
                        <SelectedFilterName>{filterCategory}</SelectedFilterName>
                        <RemoveItemButton onClick={() => onRemoveFilter(filterType.category, filterCategory)}>
                            <motion.span />
                            <motion.span />
                        </RemoveItemButton>
                    </SelectedFilterItem>
                ));
            })}

            {activeFilters.length > 0 && (
                <ClearAllItem role="button" tabIndex={0} onClick={onClearFilters}>
                    {translate('yat_nft_manager_filter_clear_all')}
                </ClearAllItem>
            )}
        </SelectedFiltersListWrapper>
    );
}
